/* ==========================================================================
   WAYPOINT — floating glass-pill bottom nav. 5 tabs, ember active dot.
   Now / Trips / Log / Map / More. The first four are places you go
   repeatedly while planning or driving; everything you go to occasionally and
   deliberately — Scooty, Bucket list, Blog, Memories, Journal, Settings —
   lives behind More, which opens a sheet rather than navigating (see
   MoreSheet.tsx for why, and for what it costs). Trips is the planner (a trip
   is a container: route, rig rules, leg math); the Log is the flat cross-trip
   timeline of stays, activities, and memories, with type + status filters and
   the insights strip at its head; Now is the windshield — tonight's stop, the
   next drive, what to mark (docs/LOG-AND-TRIP.md §9). Memories folded INTO
   the Log as a filter rather than keeping a tab — a memory is a log entry (a
   place, a date, something a human wrote). The /memories and /journal routes
   stay mounted for deep links.

   Scooty gave up its slot to Now on 2026-09-05: "we don't really use that too
   much" (Jeff), while the travel-day screen — the one read at 60mph — sat
   three taps deep under More. The chat's ember unread badge (polled ~20s +
   refetched on tab focus) moved with it onto the More tab, so an unread
   message from the other person is still one glance away.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, navigate } from '../lib/router';
import { tick } from '../lib/haptics';
import { loadChatUnread, useStoreSelector } from '../lib/store';
import { RouteIcon, MapIcon, CompassIcon, LogIcon, MoreIcon } from './icons';
import { MoreSheet, MORE_ITEMS } from './MoreSheet';

const UNREAD_POLL_MS = 20_000;

interface Tab {
  path: string;
  label: string;
  icon: typeof RouteIcon;
  match: (path: string) => boolean;
}

const TABS: Tab[] = [
  // Far left on purpose: the thumb's first stop, for the screen opened most often while moving
  // ("Now should be on far left" — Jeff, 2026-09-05).
  { path: '/today', label: 'Now', icon: CompassIcon, match: (p) => p === '/today' },
  { path: '/', label: 'Trips', icon: RouteIcon, match: (p) => p === '/' || p.startsWith('/trips') },
  // '/stays' is the Log's old address and still redirects there.
  {
    path: '/log',
    label: 'Log',
    icon: LogIcon,
    match: (p) => p.startsWith('/log') || p.startsWith('/stays'),
  },
  { path: '/map', label: 'Map', icon: MapIcon, match: (p) => p.startsWith('/map') },
  // Opens the sheet instead of navigating — `path` is a stable key and a
  // fallback destination, never somewhere this tab actually goes. It lights up
  // for every route the sheet can reach, so a deep link into Settings or a post
  // still shows you which part of the app you are standing in.
  {
    path: '/more',
    label: 'More',
    icon: MoreIcon,
    // '/posts' is the Journal's old address and redirects there; it is matched too so the tab
    // does not blink dark for the frame between the old URL and the new one.
    match: (p) => MORE_ITEMS.some((i) => p.startsWith(i.path)) || p.startsWith('/posts'),
  },
];

/** Polls /api/chat/unread on an interval and whenever the tab regains focus
 * — kept as its own hook so BottomNav stays mountable even before a user is
 * authed without throwing on a 401 (checkAuth already gates when this
 * mounts in practice, but a failed poll here is silently swallowed either
 * way, matching every other background-refresh pattern in the app). */
function useChatUnreadPolling() {
  const authUser = useStoreSelector((s) => s.authUser);
  const authUserId = authUser?.id ?? null;

  useEffect(() => {
    if (!authUserId) return;
    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      loadChatUnread().catch(() => {});
    };
    poll();
    const timer = window.setInterval(poll, UNREAD_POLL_MS);
    function onFocus() {
      if (document.visibilityState === 'visible') poll();
    }
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [authUserId]);
}

/** Visible tab order index for a given path, used by Screen.tsx to derive
 * shared-axis transition direction from nav position rather than a
 * lexicographic string compare (which disagrees with tab order for
 * '/journal' vs '/map'). A trip detail push (`/trips/:id`) has no tab of
 * its own and returns -1 — callers treat that as "always push forward". */
export function tabIndexForPath(path: string): number {
  return TABS.findIndex((t) => t.match(path));
}

export function BottomNav() {
  const path = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Close on any route change, including ones this sheet did not cause — a browser Back with the
  // sheet open would otherwise leave it sitting over a screen it has nothing to do with. Cheap,
  // and it makes the sheet's lifetime a property of the route rather than of who closed it.
  useEffect(() => {
    setMoreOpen(false);
  }, [path]);
  const chatUnread = useStoreSelector((s) => s.chatUnread);
  useChatUnreadPolling();

  const totalUnread = chatUnread.reduce((sum, t) => sum + t.unread, 0);
  const anyMentioned = chatUnread.some((t) => t.mentioned);
  const showUnreadBadge = totalUnread > 0 || anyMentioned;

  return (
    <>
    <nav className="bottom-nav" aria-label="Primary">
      <ul className="bottom-nav__pill">
        {TABS.map((tab) => {
          const active = tab.match(path);
          const Icon = tab.icon;
          // The badge sits on More because Scooty lives behind it now.
          const carriesBadge = tab.path === '/more';
          return (
            <li key={tab.path}>
              <motion.button
                type="button"
                className="bottom-nav__tab"
                aria-current={active ? 'page' : undefined}
                aria-label={
                  carriesBadge && showUnreadBadge
                    ? `${tab.label}, ${totalUnread} unread in Scooty`
                    : tab.label
                }
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 550, damping: 32 }}
                onClick={() => {
                  tick();
                  if (tab.path === '/more') {
                    setMoreOpen(true);
                    return;
                  }
                  navigate(tab.path);
                }}
              >
                <span className="bottom-nav__icon-wrap">
                  <Icon className="bottom-nav__icon" />
                  {carriesBadge && showUnreadBadge && (
                    <span className="bottom-nav__badge" aria-hidden="true">
                      {totalUnread > 0 ? (totalUnread > 9 ? '9+' : totalUnread) : ''}
                    </span>
                  )}
                </span>
                <span className="bottom-nav__label">{tab.label}</span>
                {active && (
                  <motion.span
                    layoutId="bottom-nav-dot"
                    className="bottom-nav__dot"
                    transition={{ type: 'spring', stiffness: 420, damping: 38, mass: 0.9 }}
                  />
                )}
              </motion.button>
            </li>
          );
        })}
      </ul>

      <style>{`
        .bottom-nav {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 40;
          display: flex;
          justify-content: center;
          padding: 0 12px calc(12px + var(--sab));
          pointer-events: none;
        }
        .bottom-nav__pill {
          pointer-events: auto;
          display: flex;
          gap: 2px;
          padding: 6px;
          background: color-mix(in srgb, var(--canvas-raised) 82%, transparent);
          backdrop-filter: blur(20px) saturate(1.4);
          -webkit-backdrop-filter: blur(20px) saturate(1.4);
          border: 1px solid color-mix(in srgb, var(--cloud) 70%, transparent);
          border-radius: var(--radius-pill);
          box-shadow: var(--shadow-float);
          max-width: 420px;
          width: 100%;
        }
        /* Without flex on the li wrappers the tabs shrink-wrap and cluster
           on the left of the pill instead of sharing its width. */
        .bottom-nav__pill > li { flex: 1; min-width: 0; }
        .bottom-nav__tab { padding: 0 2px; }
        .bottom-nav__tab {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          width: 100%;
          min-width: 44px;
          min-height: 52px;
          border-radius: var(--radius-pill);
          color: var(--ink-soft);
        }
        .bottom-nav__tab[aria-current='page'] {
          color: var(--ember);
        }
        .bottom-nav__icon-wrap {
          position: relative;
          display: block;
        }
        .bottom-nav__icon {
          display: block;
        }
        .bottom-nav__badge {
          position: absolute;
          top: -4px;
          right: -6px;
          min-width: 8px;
          height: 8px;
          padding: 0;
          border-radius: 50%;
          background: var(--ember);
          border: 1.5px solid var(--canvas-raised);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 9px;
          font-weight: 700;
          line-height: 1;
          color: var(--canvas-raised);
        }
        /* Mention-only badge (count 0, dot still shown) stays the plain
           8x8 dot above; a real count grows it into a small pill so the
           digit(s) are actually legible. */
        .bottom-nav__badge:not(:empty) {
          min-width: 15px;
          height: 15px;
          padding: 0 3px;
        }
        /* At five tabs a 390px-wide phone gives each ~74px of pill; "Settings"
           is the longest label and needs the tighter size + a hard clamp, or
           it wraps and shoves the row to two lines. */
        .bottom-nav__label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.01em;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .bottom-nav__dot {
          position: absolute;
          top: 4px;
          right: calc(50% - 15px);
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--ember);
        }
      `}</style>
    </nav>
      {/* Mounted directly rather than through AnimatePresence, and that is deliberate.
          Every other sheet in the app wraps in AnimatePresence for the slide-out, but every other
          sheet also lives INSIDE a screen that unmounts on navigation — which hides a bug this one
          cannot hide. BottomNav persists across routes, and here AnimatePresence ran the exit
          animation to completion and then never removed the node: the sheet sat at
          translateY(100%), invisible, with its backdrop still covering the app and body scroll
          still locked. The app looked dead. Losing the 200ms slide-out is a fair trade for a nav
          that cannot wedge itself shut. */}
      {moreOpen && <MoreSheet onClose={() => setMoreOpen(false)} />}
    </>
  );
}
